import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useT } from '../lib/i18n.js';
import { NavItem } from './NavItem.jsx';

/**
 * The admins' custom pages (content module, /p/<slug>) that the shell links to: in the header menu,
 * the client sidebar or the footer. Clients-only pages come back only for a signed-in client; the
 * titles are in the visitor's language. Without the content module (or offline) there are none.
 */
export function useSitePages() {
  const { me, language } = useAuth();
  const q = useQuery({
    queryKey: ['site-pages', me?.client?.uuid ?? 'guest', language],
    queryFn: () => api('/api/public/pages/menu'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const pages = q.data?.data ?? [];
  return {
    header: pages.filter((p) => p.placement === 'header'),
    sidebar: pages.filter((p) => p.placement === 'sidebar'),
    footer: pages.filter((p) => p.placement === 'footer'),
  };
}

/** The header menu across the top of the store and client area. */
export function SiteHeaderMenu({ pages }) {
  const t = useT();
  if (!pages.length) return null;
  return (
    <nav aria-label={t('shell.site_menu')} className="border-b border-line bg-surface px-4 md:px-10">
      <ul className="flex flex-wrap gap-x-5 gap-y-1 py-2 text-sm">
        {pages.map((p) => (
          <li key={p.slug}>
            <Link to={`/p/${p.slug}`} className="font-semibold text-muted hover:text-text hover:underline">
              {p.title}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** The sidebar section with the pages placed there. */
export function SiteSidebarPages({ pages }) {
  const t = useT();
  if (!pages.length) return null;
  return (
    <div className="mt-4">
      <p className="mb-1 px-3 text-xs font-semibold text-white/60">{t('nav.section.pages')}</p>
      {pages.map((p) => (
        <NavItem key={p.slug} to={`/p/${p.slug}`}>
          <span className="truncate">{p.title}</span>
        </NavItem>
      ))}
    </div>
  );
}

/** Footer links (next to the accessibility statement). */
export function SiteFooterLinks({ pages }) {
  return pages.map((p) => (
    <Link key={p.slug} to={`/p/${p.slug}`} className="underline-offset-2 hover:text-text hover:underline">
      {p.title}
    </Link>
  ));
}
